import { useEffect, useState } from "react";
import { addHours, nextMonday, setHours, startOfTomorrow } from "date-fns";
import { Check, ChevronDown, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PEOPLE, spaceOf, SUPPORT_EMAIL } from "@/lib/spaces";
import { splitRepeat, toLocalInput, UNITS } from "@/lib/time";
import { Field, ProjectPicker, SpacePicker, SubmitRow } from "@/components/forms/Fields";
import { PriorityPicker } from "@/components/forms/PriorityPicker";
import { useSettings } from "@/context/SettingsContext";
import { DuplicateHint, QuickFill } from "@/components/ai/QuickFill";

const PRESETS = [
  { label: "Now", get: () => null },
  { label: "In 1 hour", get: () => addHours(new Date(), 1) },
  { label: "Tomorrow 9:00", get: () => setHours(startOfTomorrow(), 9) },
  { label: "Monday 9:00", get: () => setHours(nextMonday(new Date()), 9) },
];

export const ReminderForm = ({ item, defaults, onDone }) => {
  const { user } = useAuth();
  const { space, run } = useApp();
  const { team } = useSettings();
  const r = item || {};
  const rep = splitRepeat(r.repeat_minutes);
  const [f, setF] = useState({
    title: r.title || "", details: r.details || "",
    space: r.space || defaults.space || (space !== "all" ? space : user.username),
    project_id: r.project_id ?? defaults.project_id ?? null,
    priority: r.priority || (item ? "custom" : team?.reminders?.default_priority || "medium"),
    start: r.due_at ? toLocalInput(new Date(r.due_at)) : "",
    once: item ? !r.repeat_minutes : false, every: rep.every, unit: rep.unit,
    checklist: r.checklist || [],
    assignees: r.assignees || [], silent: !!r.silent,
  });
  const [dupes, setDupes] = useState([]);
  useEffect(() => {
    if (item || f.title.trim().length < 4) { setDupes([]); return undefined; }
    const t = setTimeout(() => api.get("/reminders-similar", { params: { title: f.title } }).then((x) => setDupes(x.data.items)).catch(() => {}), 500);
    return () => clearTimeout(t);
  }, [f.title, item]);
  const fill = (d) => {
    setF((s) => ({ ...s, title: d.title || s.title, details: d.details || s.details, space: d.space || s.space, priority: d.priority || s.priority,
      start: d.due_at ? toLocalInput(new Date(d.due_at)) : s.start, checklist: (d.checklist || []).map((text) => ({ text, done: false })) }));
    if (d.due_at || d.details) setMore(true);
  };
  const [initialStart] = useState(f.start);
  const [more, setMore] = useState(!!item || !!defaults.project_id);
  const [busy, setBusy] = useState(false);
  const custom = f.priority === "custom";
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      title: f.title, details: f.details, space: f.space, project_id: f.project_id, priority: f.priority,
      repeat_minutes: custom && !f.once ? Number(f.every) * f.unit : 0,
      assignees: f.assignees.filter((a) => a !== f.space), silent: f.silent,
    };
    if (!item && f.checklist.length) payload.checklist = f.checklist;
    if (!item || f.start !== initialStart) payload.due_at = (f.start ? new Date(f.start) : new Date()).toISOString();
    const ok = await run(() => (item ? api.patch(`/reminders/${item.id}`, payload) : api.post("/reminders", payload)), item ? "Reminder updated" : "Reminder set");
    setBusy(false);
    if (ok) onDone();
  };

  const owner = spaceOf(f.space);
  const creator = spaceOf(r.created_by || user.username);
  const recipients = [...new Set([owner.email, creator.email, ...f.assignees.filter((a) => a !== f.space).map((a) => spaceOf(a).email), SUPPORT_EMAIL])];
  const toggleAssignee = (k) => setF((s) => ({ ...s, assignees: s.assignees.includes(k) ? s.assignees.filter((a) => a !== k) : [...s.assignees, k] }));

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="reminder-form">
      {!item && <QuickFill onFill={fill} />}
      <Field label="What's the reminder?">
        <Input autoFocus value={f.title} onChange={set("title")} placeholder="Chase renewal fees for client X" required data-testid="reminder-title-input" className="h-11" />
        <DuplicateHint items={dupes} />
        {!item && f.checklist.length > 0 && <p className="text-xs text-muted-foreground" data-testid="reminder-checklist-preview">Checklist: {f.checklist.map((c) => c.text).join(" · ")}</p>}
      </Field>
      <Field label="Priority"><PriorityPicker value={f.priority} onChange={set("priority")} /></Field>
      {custom && (
        <div className="fade-up space-y-4 rounded-xl border bg-background/40 p-4" data-testid="custom-timing-panel">
          <div className={`flex items-center gap-2 ${f.once ? "pointer-events-none opacity-40" : ""}`}>
            <span className="text-sm text-muted-foreground">Email every</span>
            <Input type="number" min={1} disabled={f.once} value={f.every} onChange={set("every")} className="w-20 font-mono" data-testid="reminder-repeat-every-input" />
            <Select disabled={f.once} value={String(f.unit)} onValueChange={(v) => set("unit")(Number(v))}>
              <SelectTrigger className="w-32" data-testid="reminder-repeat-unit-select"><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u.value} value={String(u.value)}>{u.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>Just once, at the start time</span>
            <Switch checked={f.once} onCheckedChange={set("once")} data-testid="reminder-once-switch" />
          </label>
        </div>
      )}
      <Field label="Whose space"><SpacePicker value={f.space} onChange={set("space")} /></Field>
      <Field label="Also assign to" hint="They'll see it in their space and get the emails too.">
        <div className="flex flex-wrap gap-1.5" data-testid="reminder-assignees">
          {PEOPLE.filter((p) => p.key !== f.space).map((p) => {
            const on = f.assignees.includes(p.key);
            return (
              <button key={p.key} type="button" onClick={() => toggleAssignee(p.key)} data-testid={`reminder-assignee-${p.key}`}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? "text-foreground" : "text-muted-foreground hover:bg-white/5"}`}
                style={on ? { borderColor: p.color, background: `${p.color}22` } : undefined}>
                <span className="h-2 w-2 rounded-full" style={{ background: p.color }} /> {p.name}{on && <Check className="h-3 w-3" />}
              </button>
            );
          })}
        </div>
      </Field>
      <label className="flex items-center justify-between gap-3 rounded-xl border bg-background/40 px-4 py-3 text-sm">
        <span><span className="font-medium">No emails for this reminder</span><span className="block text-xs text-muted-foreground">Nobody gets emailed, not even support. It still shows on the dashboard and TV.</span></span>
        <Switch checked={f.silent} onCheckedChange={set("silent")} data-testid="reminder-silent-switch" />
      </label>
      <button type="button" onClick={() => setMore((m) => !m)} data-testid="reminder-more-options" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${more ? "rotate-180" : ""}`} /> More options
        {!more && <span className="normal-case tracking-normal">· starts {f.start ? "later" : "now"}</span>}
      </button>
      {more && (
        <div className="fade-up space-y-5">
          <Field label="Starts" hint={f.start ? "First email at this time." : "First email goes out right away (within 9am–5pm)."}>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => {
                const v = p.get();
                return (
                  <button key={p.label} type="button" onClick={() => set("start")(v ? toLocalInput(v) : "")} data-testid={`reminder-preset-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors hover:bg-white/5 ${!v && !f.start ? "border-white/40 text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{p.label}</button>
                );
              })}
            </div>
            <Input type="datetime-local" value={f.start} onChange={set("start")} data-testid="reminder-due-input" className="h-10 font-mono" />
          </Field>
          <Field label="Details (optional)">
            <Textarea value={f.details} onChange={set("details")} rows={2} placeholder="Anything useful to include in the email" data-testid="reminder-details-input" />
          </Field>
          <Field label="Project (optional)"><ProjectPicker value={f.project_id} onChange={set("project_id")} /></Field>
        </div>
      )}
      <p className="flex items-start gap-2 rounded-lg bg-white/[0.03] px-3 py-2 text-xs text-muted-foreground" data-testid="reminder-recipients">
        <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {f.silent ? "No emails will be sent" : `Emails ${recipients.join(", ")}`}
      </p>
      <SubmitRow busy={busy} label={item ? "Save reminder" : "Set reminder"} onCancel={onDone} testid="reminder-submit-button" />
    </form>
  );
};
